import { Card, Empty, Flex, Table } from 'antd';
import { BaseButton } from 'components/button/button-base';
import { ErrorListDisplay } from 'components/ErrorDisplay';
import { Subtitle, Text } from 'components/mvp-typography';
import { useBuyAssessmentMutation } from 'lib/hooks/tanstack/use-billing-buyAssessmentMutation';
import { useGetProductPrice } from 'lib/hooks/tanstack/use-getProductPrice';
import { useClinicianContext } from 'lib/hooks/use-resolveCurrentClinicInfo';
import useSearchParams from 'lib/hooks/use-searchParams';
import { resolveError } from 'lib/utils/formatters/error/resolve-error';
import { useErrorList } from 'lib/utils/useErrorList';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ClinicianRoutePrefix } from 'routes';
import {
  BillingReceiptContent,
  BillingReceiptContentProps
} from './_components/receipt-content';
import { PurchaseLayout } from './_components/PurchaseLayout';
import { CartItem, useCart } from './context-cart';
import { InternationalNumberFormatter } from './formatNumber';
import { ColumnType } from 'antd/lib/table';
import { formatProductName } from './formatProductName';

/**
 * Internal component that displays a price summary and handles the purchase action.
 */
function PriceSummaryAndPurchase(props: {
  setPurchaseResult: (
    result: Omit<BillingReceiptContentProps, 'onExit'>
  ) => void;
}) {
  const { cart, dispatch } = useCart();
  const clinicianInfo = useClinicianContext();
  const purchaseMutation = useBuyAssessmentMutation();
  const errorList = useErrorList();

  const item = cart[0];
  const productPricingQuery = useGetProductPrice({
    type: item?.type,
    productType: item.isBulk ? 'bulk' : 'on-demand',
    quantity: item?.quantity ?? 0
  });

  function handlePurchase() {
    const item = cart[0];
    if (!purchaseMutation.isLoading) {
      errorList.reset();
      purchaseMutation.mutate(
        {
          type: item.type,
          units: item.quantity,
          isBulk: item.isBulk ?? false,
          clinicId: clinicianInfo.currentClinic
        },
        {
          onSuccess: out => {
            if (!out.data.success) return;

            dispatch({ type: 'CLEAR_CART' });
            props.setPurchaseResult!({
              invoiceId: out.data.invoiceId,
              invoiceNumber: out.data.invoiceNumber,
              date: new Date().toISOString().slice(0, 10),
              price: {
                value: out.data.amount,
                currency: out.data.currency as any
              }
            });
          },
          onError: error => {
            errorList.addError(
              resolveError(error),
              'handlePurchase',
              `Attempted purchase`
            );
          }
        }
      );
    }
  }

  const columns: ColumnType<CartItem>[] = [
    {
      title: 'Product',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => formatProductName(type)
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (quantity: number) =>
        InternationalNumberFormatter.format(quantity)
    },
    {
      title: 'Price Per Unit',
      dataIndex: 'pricePerUnit',
      key: 'pricePerUnit',
      render: (price: number) =>
        `$${InternationalNumberFormatter.format(price)}`
    },
    {
      title: 'Savings',
      key: 'savings',
      render: () => {
        const data = productPricingQuery.data?.data;
        if (!data) return '-';
        return <Text>{(data.discount * 100).toFixed(2)}%</Text>;
      }
    },
    {
      title: 'Total',
      key: 'total',
      render: (_val, record) => {
        const total =
          productPricingQuery.data?.data.totalPrice ??
          record.pricePerUnit * record.quantity;
        return `$${InternationalNumberFormatter.format(total)}`;
      }
    }
  ];

  const finalPrice =
    productPricingQuery.data?.data.totalPrice ??
    cart[0].quantity * cart[0].pricePerUnit;

  return (
    <Card>
      <Subtitle>Your Cart</Subtitle>
      <Text>
        Take a moment to check the items in your cart before continuing. You
        will not be able to return to this step after you continue.
      </Text>
      <Table
        dataSource={cart.slice(0, 1)}
        columns={columns}
        pagination={false}
        loading={productPricingQuery.isLoading}
        rowKey={(record, index) => `${record.type}-${index}`}
      />

      <div style={{ marginTop: '20px', textAlign: 'right' }}>
        <Text strong style={{ fontSize: '1.2em' }}>
          Final Price: ${InternationalNumberFormatter.format(finalPrice)}
        </Text>
      </div>

      <Flex vertical justify="center" align="center" gap={16}>
        <Flex
          vertical
          gap={16}
          style={{
            marginTop: '2rem',
            // maxWidth: '500px'
          }}
        >
          <Flex justify="center" vertical>
            <Flex justify='center'>
              <BaseButton
                type="primary"
                size="large"
                onClick={handlePurchase}
                loading={purchaseMutation.isLoading}
              >
                Purchase
              </BaseButton>
            </Flex>
            <Text type="secondary">
              <small>
                By clicking the &quot;purchase&quot; button you authorize your
                billing account being charged. The payment is non-refundable and
                your balance will be updated within 1-2 business days.
              </small>
            </Text>
          </Flex>
        </Flex>
        <ErrorListDisplay errorList={errorList} />
      </Flex>
    </Card>
  );
}

/**
 * Main checkout page component that handles the entire purchase flow.
 */
export function BillingCheckoutPage() {
  const cart = useCart();
  const [purchaseResult, setPurchaseResult] = useState<Omit<
    BillingReceiptContentProps,
    'onExit'
  > | null>(null);
  const history = useHistory();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  /**
   * Handle user clicking "Exit" on the receipt page.
   * Redirects back to the original destination or clinic home.
   */
  const handleExitReceipt = () => {
    setPurchaseResult(null);
    const redirectTo = returnTo
      ? decodeURIComponent(returnTo)
      : ClinicianRoutePrefix;

    const newSearchParams = new URLSearchParams();
    searchParams.forEach((value, key) => {
      if (key !== 'returnTo') {
        // Preserve existing search params except 'returnTo'
        newSearchParams.set(key, value);
      }
    });

    history.push({
      pathname: redirectTo,
      search: newSearchParams.toString()
    });
  };

  /**
   * If a purchase was successful, display the receipt.
   */
  if (purchaseResult) {
    return (
      <PurchaseLayout title="Receipt">
        <Card>
          <BillingReceiptContent
            invoiceId={purchaseResult.invoiceId}
            invoiceNumber={purchaseResult.invoiceNumber}
            date={purchaseResult.date}
            price={purchaseResult.price}
            onExit={handleExitReceipt}
          />
        </Card>
      </PurchaseLayout>
    );
  }

  if (cart.cart.length === 0) {
    return (
      <PurchaseLayout title="Checkout" onBack={() => history.goBack()}>
        <Empty
          description={
            <Text type="secondary">
              Your cart is empty. Add items to checkout.
            </Text>
          }
        />
      </PurchaseLayout>
    );
  }

  /**
   * Otherwise, display the price summary and purchase button.
   */
  return (
    <PurchaseLayout
      title="Checkout"
      onBack={() => {
        cart.dispatch({
          type: 'CLEAR_CART'
        });
        history.goBack();
      }}
    >
      <PriceSummaryAndPurchase setPurchaseResult={setPurchaseResult} />
    </PurchaseLayout>
  );
}
